import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, MapPin, Receipt, CreditCard, CheckCircle2 } from 'lucide-react';

interface MissingSettings {
  address: boolean;
  taxInfo: boolean;
  stripeConnect: boolean;
}

export const ArtistSettingsReminder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [missingSettings, setMissingSettings] = useState<MissingSettings>({
    address: false,
    taxInfo: false,
    stripeConnect: false,
  });
  const [isArtist, setIsArtist] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || checked) return;

    const checkArtistSettings = async () => {
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('user_type, onboarding_completed, street_address, city, postal_code, country, tax_info_complete, tax_id, tax_country, stripe_connect_onboarding_completed, stripe_connect_charges_enabled')
          .eq('id', user.id)
          .single();

        if (error || !profile) {
          setChecked(true);
          return;
        }

        // Only check for artists/sellers
        if (profile.user_type !== 'artist') {
          setChecked(true);
          return;
        }

        // Don't show during onboarding
        if (!profile.onboarding_completed) {
          setChecked(true);
          return;
        }

        setIsArtist(true);

        // Check for missing settings
        const missing: MissingSettings = {
          address: !profile.street_address || !profile.city || !profile.postal_code || !profile.country,
          taxInfo: !profile.tax_info_complete || !profile.tax_id || !profile.tax_country,
          stripeConnect: !profile.stripe_connect_onboarding_completed || !profile.stripe_connect_charges_enabled,
        };

        setMissingSettings(missing);

        // Show dialog if any setting is missing
        const hasMissing = missing.address || missing.taxInfo || missing.stripeConnect;
        
        // Check session storage to avoid showing popup multiple times per session
        const dismissedKey = `artist_settings_reminder_${user.id}`;
        const dismissed = sessionStorage.getItem(dismissedKey);
        
        if (hasMissing && !dismissed) {
          setOpen(true);
        }

        setChecked(true);
      } catch (err) {
        console.error('Error checking artist settings:', err);
        setChecked(true);
      }
    };

    // Small delay to let the app settle after login
    const timer = setTimeout(checkArtistSettings, 1000);
    return () => clearTimeout(timer);
  }, [user, checked]);

  const handleDismiss = () => {
    if (user) {
      sessionStorage.setItem(`artist_settings_reminder_${user.id}`, 'true');
    }
    setOpen(false);
  };

  const handleGoToSettings = (section: string) => {
    if (user) {
      sessionStorage.setItem(`artist_settings_reminder_${user.id}`, 'true');
    }
    setOpen(false);
    navigate(`/settings?section=${section}`);
  };

  const missingCount = Object.values(missingSettings).filter(Boolean).length;

  if (!isArtist || missingCount === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) handleDismiss();
      else setOpen(value);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Komplettera dina inställningar
          </DialogTitle>
          <DialogDescription>
            För att kunna sälja konstverk och ta emot betalningar behöver du fylla i följande:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* Address */}
          <div 
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              missingSettings.address 
                ? 'border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10' 
                : 'border-green-500/50 bg-green-500/5'
            }`}
            onClick={() => missingSettings.address && handleGoToSettings('shipping')}
          >
            {missingSettings.address ? (
              <MapPin className="h-5 w-5 text-amber-500 shrink-0" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Adress</p>
              <p className="text-xs text-muted-foreground">
                {missingSettings.address 
                  ? 'Krävs för fraktberäkning av originalverk' 
                  : 'Ifylld'
                }
              </p>
            </div>
          </div>

          {/* Tax Info */}
          <div 
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              missingSettings.taxInfo 
                ? 'border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10' 
                : 'border-green-500/50 bg-green-500/5'
            }`}
            onClick={() => missingSettings.taxInfo && handleGoToSettings('tax')}
          >
            {missingSettings.taxInfo ? (
              <Receipt className="h-5 w-5 text-amber-500 shrink-0" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Skatteinformation</p>
              <p className="text-xs text-muted-foreground">
                {missingSettings.taxInfo 
                  ? 'Krävs för DAC7-rapportering' 
                  : 'Ifylld'
                }
              </p>
            </div>
          </div>

          {/* Stripe Connect */}
          <div 
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              missingSettings.stripeConnect 
                ? 'border-amber-500/50 bg-amber-500/5 hover:bg-amber-500/10' 
                : 'border-green-500/50 bg-green-500/5'
            }`}
            onClick={() => missingSettings.stripeConnect && handleGoToSettings('payments')}
          >
            {missingSettings.stripeConnect ? (
              <CreditCard className="h-5 w-5 text-amber-500 shrink-0" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">Betalningskonto</p>
              <p className="text-xs text-muted-foreground">
                {missingSettings.stripeConnect 
                  ? 'Krävs för att ta emot utbetalningar' 
                  : 'Kopplat'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={handleDismiss}>
            Påminn mig senare
          </Button>
          <Button onClick={() => handleGoToSettings('shipping')}>
            Gå till inställningar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
